import React, { useContext, useEffect, useMemo } from "react";
import { withEmotionCache } from "@emotion/react";
import { cssBundleHref } from "@remix-run/css-bundle";
import {
  Links,
  LiveReload,
  Meta,
  Scripts,
  ScrollRestoration,
  useLoaderData,
  useRouteError } from
"@remix-run/react";
import {
  json,
  type LinksFunction,
  type LoaderArgs } from
"@remix-run/cloudflare"; // Depends on the runtime you choose

import styles from "chakra/theme/css/App.css";

import { ServerStyleContext, ClientStyleContext } from "./chakra/context";
import { ChakraProvider, cookieStorageManagerSSR } from "@chakra-ui/react";
import MainLayout from "layouts/main.layout";
import theme from "chakra/theme/theme";
import { i18next } from "i18next.server";
import { useTranslation } from "react-i18next";
import { useChangeLanguage } from "remix-i18next";

import "@fontsource/noto-sans/400.css?__remix_sideEffect__";
import "@fontsource/noto-sans/500.css?__remix_sideEffect__";
import "@fontsource/noto-sans/700.css?__remix_sideEffect__";
import posthog from "posthog-js";

export const links: LinksFunction = () => {
  const links = [
  {
    rel: "stylesheet",
    href: styles
  },
  {
    rel: "apple-touch-icon",
    sizes: "180x180",
    href: "/apple-touch-icon.png"
  },
  {
    rel: "icon",
    type: "image/png",
    sizes: "32x32",
    href: "/favicon-32x32.png"
  },
  {
    rel: "icon",
    type: "image/png",
    sizes: "16x16",
    href: "/favicon-16x16.png"
  },
  { rel: "manifest", href: "/site.webmanifest" },
  { rel: "mask-icon", href: "/safari-pinned-tab.svg", color: "#5bbad5" }];


  if (cssBundleHref) {
    links.push({ rel: "stylesheet", href: cssBundleHref });
  }

  return links;
};

export const loader = async ({ request }: LoaderArgs) => {
  const locale = await i18next.getLocale(request);
  // first time users will not have any cookies and you may not return
  // undefined here, hence ?? is necessary
  return json({
    cookie: request.headers.get("cookie") ?? "",
    locale
  });
};

interface DocumentProps {
  children: React.ReactNode;
}

export const handle = {
  // In the handle export, we can add a i18n key with namespaces our route
  // will need to load. This key can be a single string or an array of strings.
  // TIP: In most cases, you should set this to your defaultNS from your i18n config
  // or if you did not set one, set it to the i18next default namespace "translation"
  i18n: "common"
};

const Document = withEmotionCache(
  ({ children }: DocumentProps, emotionCache) => {
    const serverStyleData = useContext(ServerStyleContext);
    const clientStyleData = useContext(ClientStyleContext);

    // Only executed on client
    useEffect(() => {
      // re-link sheet container
      emotionCache.sheet.container = document.head;
      // re-inject tags
      const tags = emotionCache.sheet.tags;
      emotionCache.sheet.flush();
      tags.forEach((tag) => {
        (emotionCache.sheet as any)._insertTag(tag);
      });
      // reset cache to reapply global styles
      clientStyleData?.reset();
    }, []);

    const data = useLoaderData<typeof loader>();

    const { i18n } = useTranslation();

    // This hook will change the i18n instance language to the current locale
    // detected by the loader, this way, when we do something to change the
    // language, this locale will change and i18next will load the correct
    // translation files
    useChangeLanguage(data.locale);

    let cookies = data.cookie;

    // the client get the cookies from the document
    // because when we do a client routing, the loader can have stored an outdated value
    if (typeof document !== "undefined") {
      cookies = document.cookie;
    }

    // get and store the color mode from the cookies.
    // It'll update the cookies if there isn't any and we have set a default value
    const colorMode = useMemo(() => {
      let color = getColorMode(cookies);

      if (!color && DEFAULT_COLOR_MODE) {
        cookies += ` ${CHAKRA_COOKIE_COLOR_KEY}=${DEFAULT_COLOR_MODE}`;
        color = DEFAULT_COLOR_MODE;
      }

      return color;
    }, [cookies]);

    return (
      <html
        lang={data.locale}
        dir={i18n.dir()}
        {...colorMode && {
          "data-theme": colorMode,
          style: { colorScheme: colorMode }
        }}>

        <head>
          <meta name="viewport" content="width=device-width,initial-scale=1" />
          <meta charSet="utf-8" />
          <meta title="GoTips" />
          <meta name="theme-color" content="#000000" />
          <meta name="msapplication-TileColor" content="#da532c" />
          <Meta />
          <Links />
          {serverStyleData?.map(({ key, ids, css }) =>
          <style
            key={key}
            data-emotion={`${key} ${ids.join(" ")}`}
            dangerouslySetInnerHTML={{ __html: css }} />

          )}
        </head>
        <body
          {...colorMode && {
            className: `chakra-ui-${colorMode}`
          }}>

          <ChakraProvider
            colorModeManager={cookieStorageManagerSSR(cookies)}
            theme={theme}>

            {children}
          </ChakraProvider>
          <ScrollRestoration />
          <Scripts />
          <LiveReload />
        </body>
      </html>);

  }
);

function getColorMode(cookies: string) {
  const match = cookies.match(
    new RegExp(`(^| )${CHAKRA_COOKIE_COLOR_KEY}=([^;]+)`)
  );
  return match == null ? void 0 : match[2];
}

// here we can set the default color mode. If we set it to null,
// there's no way for us to know what is the the user's preferred theme
// so the cient will have to figure out and maybe there'll be a flash the first time the user visits us.
const DEFAULT_COLOR_MODE: "dark" | "light" | null = "light";

const CHAKRA_COOKIE_COLOR_KEY = "chakra-ui-color-mode";

export const ErrorBoundary = () => {
  const error = useRouteError();
  posthog.captureException(error);
  return <div>Something went wrong</div>;
};

export default function App() {
  return (
    <Document>
      <MainLayout />
    </Document>);

}